/**
 * LoginPage - Admin and Delegate login
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { authAPI, eventAPI, getErrorMessage } from '@/lib/api';
import api from '@/lib/api';
import afterLogin from '@/lib/afterLogin';
import { PublicLayout } from '@/components/layouts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Shield, User, Loader2, LogIn, ChefHat, CreditCard, Building2 } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("admin");
  const [isLoading, setIsLoading] = useState(false);
  const [delegateForm, setDelegateForm] = useState({ event_id: "", email: "", password: "" });
  const [adminForm, setAdminForm] = useState({ email: "", password: "", remember_me: false });

  // Helper to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  };

  // Fetch app config for logo and app name
  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn: async () => {
      const response = await api.get('/app-config');
      return response.data;
    },
  });

  const { data: events } = useQuery({
    queryKey: ["events"],
    queryFn: () => eventAPI.list().then((r) => r.data),
  });

  const handleDelegateLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Clear any existing auth state first to prevent stale token issues
    localStorage.removeItem("eventflow_token");
    localStorage.removeItem("eventflow-auth");
    useAuthStore.getState().logout();
    
    try {
      // Only send event_id if it's selected
      const loginData = { 
        email: delegateForm.email, 
        password: delegateForm.password 
      };
      if (delegateForm.event_id) {
        loginData.event_id = delegateForm.event_id;
      }
      
      const response = await authAPI.delegateLogin(loginData);
      const { access_token, event_id, event_name, delegate_name } = response.data;
      
      // Store token in localStorage first (for API interceptor fallback)
      localStorage.setItem("eventflow_token", access_token);
      
      // Fetch profile with the new token
      const profile = await authAPI.getDelegateProfile();
      
      // Update zustand store
      useAuthStore.getState().setAuth(access_token, profile.data, "delegate", event_id);
      
      // Small delay to ensure store persistence completes before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast.success(`Welcome back${delegate_name ? `, ${delegate_name}` : ''}!`);
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType: 'delegate', tenantId: null, redirect });
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Clear any existing auth state first to prevent stale token issues
    localStorage.removeItem("eventflow_token");
    localStorage.removeItem("eventflow-auth");
    useAuthStore.getState().logout();
    
    try {
      const response = await authAPI.adminLogin(adminForm);
      const { access_token, permissions } = response.data;
      
      // Store token in localStorage first (for API interceptor fallback)
      localStorage.setItem("eventflow_token", access_token);
      
      // Fetch profile with the new token
      const profile = await authAPI.getAdminProfile();
      
      // Update zustand store - this also persists to localStorage via middleware
      useAuthStore.getState().setAuth(access_token, profile.data, "admin", null, permissions);
      
      // Small delay to ensure store persistence completes before navigation
      // This prevents race condition where RequireAuth checks before hydration
      await new Promise(resolve => setTimeout(resolve, 100));
      
      toast.success("Welcome back, Admin!");
      const redirect = new URLSearchParams(window.location.search).get('redirect') || '';
      await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect });
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {appConfig?.logo_url ? (
              <img 
                src={getImageUrl(appConfig.logo_url)} 
                alt={appConfig.app_name || "Logo"} 
                className="mx-auto mb-4 h-12 object-contain"
                data-testid="login-logo"
              />
            ) : (
              <div className="mx-auto mb-4 h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            )}
            <CardTitle className="text-2xl">Sign In to {appConfig?.app_name || 'EventFlow'}</CardTitle>
            <CardDescription>Access your event management portal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="admin" data-testid="tab-admin">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="delegate" data-testid="tab-delegate">
                  <User className="h-4 w-4 mr-2" />
                  Attendee
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="admin">
                <form onSubmit={handleAdminLogin} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      placeholder="Enter your email"
                      required
                      data-testid="admin-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-password">Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="admin-password-input"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={adminForm.remember_me}
                      onChange={(e) => setAdminForm({ ...adminForm, remember_me: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid="admin-remember-me"
                    />
                    Remember me for 30 days
                  </label>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="admin-login-btn">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                    Sign In as Admin
                  </Button>
                  <div className="text-center">
                    <Link to="/forgot-password?type=admin" className="text-sm text-blue-600 hover:underline">
                      Forgot your password?
                    </Link>
                  </div>
                </form>
              </TabsContent>
              
              <TabsContent value="delegate">
                <form onSubmit={handleDelegateLogin} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="event">Event (optional)</Label>
                    <Select
                      value={delegateForm.event_id}
                      onValueChange={(v) => setDelegateForm({ ...delegateForm, event_id: v })}
                    >
                      <SelectTrigger data-testid="event-select">
                        <SelectValue placeholder="Select your event (or leave blank)" />
                      </SelectTrigger>
                      <SelectContent>
                        {events?.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      If you're registered for only one event, you can leave this blank
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="delegate-email">Email</Label>
                    <Input
                      id="delegate-email"
                      type="email"
                      value={delegateForm.email}
                      onChange={(e) => setDelegateForm({ ...delegateForm, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      data-testid="delegate-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="delegate-password">Password</Label>
                    <Input
                      id="delegate-password"
                      type="password"
                      value={delegateForm.password}
                      onChange={(e) => setDelegateForm({ ...delegateForm, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      data-testid="delegate-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading} data-testid="delegate-login-btn">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                    Sign In as Attendee
                  </Button>
                  <div className="text-center">
                    <Link 
                      to={`/forgot-password?type=delegate${delegateForm.event_id ? `&event=${delegateForm.event_id}` : ''}`} 
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </form>
              </TabsContent>
            </Tabs>

            <div className="text-center text-sm text-slate-500">
              <p>New to EventFlow? <Link to="/" className="text-indigo-600 hover:underline">Register for an event</Link></p>
            </div>
            
            {/* Clear session button for stuck users */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  useAuthStore.getState().logout();
                  toast.success("Session cleared. Please login again.");
                  window.location.reload();
                }}
                className="text-xs text-slate-400 hover:text-slate-600 hover:underline"
              >
                Having trouble? Clear session data
              </button>
            </div>

            {/* Staff Login Options */}
            <div className="pt-4 border-t mt-4">
              <p className="text-center text-xs text-slate-500 mb-3">Staff & Operations Login</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/security/login">
                  <Button variant="outline" size="sm" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    Security
                  </Button>
                </Link>
                <Link to="/dining/login">
                  <Button variant="outline" size="sm" className="text-xs">
                    <ChefHat className="h-3 w-3 mr-1" />
                    Kitchen
                  </Button>
                </Link>
                <Link to="/dining/login">
                  <Button variant="outline" size="sm" className="text-xs">
                    <CreditCard className="h-3 w-3 mr-1" />
                    POS
                  </Button>
                </Link>
                <Link to="/housing/login">
                  <Button variant="outline" size="sm" className="text-xs">
                    <Building2 className="h-3 w-3 mr-1" />
                    Housing
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
};

export default LoginPage;
